import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { closeModal } from '@/store/modal/slice';
import { UseMutationResult } from '@tanstack/react-query';
import { MODAL_IDS } from 'src/enums/EModal';
import { CustomModal } from '../CustomModal';
import { Form, Input, Button, notification, Row, Col } from 'antd';
import { MIN_10_WORDS_REGEX } from 'src/constants';
import {
	Tag,
	AlertCircle,
	MessageSquare,
	Calendar,
	CreditCard,
	CheckCircle,
	Clock,
} from 'lucide-react';
import { useState } from 'react';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getModalData } from '@/store/modal/selector';
import { applyRetentionDiscount } from '@/api/user';
import { useMutation } from '@tanstack/react-query';
import { updateUserAccount } from '@/store/account/slice';
import { getLoggedInUser } from '@/store/account/selector';

interface CancelSubReasonModalProps {
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	loading?: boolean;
	onExtendTrial?: () => void;
	isExtendingTrial?: boolean;
	showCoupon?: boolean;
}

const calculateUntilDate = (modalData: any) => {
	const currentDate = new Date(modalData?.currentPeriodEnd);
	const interval = modalData?.currentSubscriptionInterval?.toLowerCase();

	if (modalData?.allowExtendTrial) {
		// For trial users, add interval to trial end date
		const trialEndDate = new Date(modalData?.trialEnd);
		if (interval === 'year') {
			trialEndDate.setFullYear(trialEndDate.getFullYear() + 1);
		} else if (interval === 'month') {
			trialEndDate.setMonth(trialEndDate.getMonth() + 1);
		}
		return trialEndDate.toLocaleDateString();
	} else {
		// For non-trial users, add interval to current period end
		if (interval === 'year') {
			currentDate.setFullYear(currentDate.getFullYear() + 1);
		} else if (interval === 'month') {
			currentDate.setMonth(currentDate.getMonth() + 1);
		}
		return currentDate.toLocaleDateString();
	}
};

const CancelSubReasonModal = ({
	mutation,
	onExtendTrial,
	isExtendingTrial = false,
	loading = false,
	showCoupon = false,
}: CancelSubReasonModalProps) => {
	const [form] = Form.useForm<{ reason: string }>();
	const [couponRedeemed, setCouponRedeemed] = useState(false);
	const dispatch = useAppDispatch();
	const loggedInUser = useAppSelector(getLoggedInUser);
	const modalData = useAppSelector(getModalData) as {
		subscriptionType: 'Inbound' | 'Outbound';
		inboundPlan?: string;
		inboundPlanStatus?: string;
		inboundTrialEnd?: string;
		inboundAllowExtendTrial?: boolean;
		inboundCurrentPeriodEnd?: string;
		inboundSubscriptionPrice?: number;
		inboundSubscriptionInterval?: string;
		outboundPlan?: string;
		outboundPlanStatus?: string;
		outboundTrialEnd?: string;
		outboundAllowExtendTrial?: boolean;
		outboundCurrentPeriodEnd?: string;
		outboundSubscriptionPrice?: number;
		outboundSubscriptionInterval?: string;
	};

	const getSubscriptionDetails = () => {
		const isInbound = modalData?.subscriptionType === 'Inbound';
		return {
			plan: isInbound ? modalData?.inboundPlan : modalData?.outboundPlan,
			status: isInbound ? modalData?.inboundPlanStatus : modalData?.outboundPlanStatus,
			trialEnd: isInbound ? modalData?.inboundTrialEnd : modalData?.outboundTrialEnd,
			allowExtendTrial: isInbound
				? modalData?.inboundAllowExtendTrial
				: modalData?.outboundAllowExtendTrial,
			currentPeriodEnd: isInbound
				? modalData?.inboundCurrentPeriodEnd
				: modalData?.outboundCurrentPeriodEnd,
			subscriptionPrice: isInbound
				? modalData?.inboundSubscriptionPrice
				: modalData?.outboundSubscriptionPrice,
			subscriptionInterval: isInbound
				? modalData?.inboundSubscriptionInterval
				: modalData?.outboundSubscriptionInterval,
		};
	};

	const subscriptionDetails = getSubscriptionDetails();

	const retentionDiscountMutation = useMutation({
		mutationKey: ['retention-discount'],
		mutationFn: () => applyRetentionDiscount(modalData?.subscriptionType),
		onSuccess: () => {
			dispatch(
				updateUserAccount({
					userAccount: {
						[modalData?.subscriptionType === 'Inbound'
							? 'inboundRetentionCouponUsed'
							: 'outboundRetentionCouponUsed']: true,
					},
				}),
			);
			notification.success({
				message: 'Retention discount applied',
			});
			setCouponRedeemed(true);
		},
		onError: (error: any) => {
			notification.error({
				message: error?.message || 'Something went wrong',
				description: error?.description || 'Please try again later',
			});
		},
	});

	const handleSubmit = async (values: {
		reason: string;
		subscriptionType: 'Inbound' | 'Outbound';
	}) => {
		try {
			await mutation.mutateAsync({
				reason: values.reason,
				subscriptionType: values.subscriptionType,
			});
			dispatch(closeModal());
		} catch (e) {
			console.error(e);
		}
	};

	const handleExpertCall = () => {
		window.open(
			'https://calendly.com/d/crj3-htt-fw8/ai-front-desk-pre-cancellation-consultation',
			'_blank',
		);
	};

	const handleClose = () => {
		setCouponRedeemed(false);
		dispatch(closeModal());
	};

	const handleCloseCouponSuccess = () => {
		setCouponRedeemed(false);
	};

	if (couponRedeemed) {
		return (
			<CustomModal
				width={'70%'}
				modalId={MODAL_IDS.CANCEL_REASON_MODAL}
				//@ts-expect-error ignore for now
				onClose={handleCloseCouponSuccess}
				footer={null}
				closable={true}
				title="Discount Applied Successfully"
			>
				<div className="text-center p-6">
					<div className="mb-4 flex justify-center">
						<div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
							<CheckCircle className="h-8 w-8 text-green-600" />
						</div>
					</div>
					<h2 className="text-xl font-bold mb-2">50% Discount Applied!</h2>
					<p className="text-gray-600 mb-4">
						Your discount has been successfully applied to your subscription. You'll be charged $
						{((subscriptionDetails?.subscriptionPrice || 0) / 2).toFixed(2)} for{' '}
						{subscriptionDetails.plan} until {calculateUntilDate(subscriptionDetails)}.
					</p>

					<div className="bg-blue-50 p-4 rounded-lg mb-6">
						<h3 className="text-lg font-semibold mb-2">We Value Your Feedback</h3>
						<p className="text-gray-600 mb-4">
							We understand that you were considering canceling your subscription. We'd like to
							learn more about your experience and any challenges you might be facing. Your feedback
							helps us improve our service for everyone.
						</p>

						<h3 className="text-lg font-semibold mb-2">Let Us Help You Succeed</h3>
						<p className="text-gray-600 mb-4">
							Many of our customers have found that a personalized consultation helps them discover
							features they weren't aware of and develop strategies to maximize their return on
							investment.
						</p>

						<Button
							type="primary"
							onClick={handleExpertCall}
							className="bg-blue-600 hover:bg-blue-700"
						>
							Schedule a Consultation with Our Expert
						</Button>
					</div>

					<Button type="default" block onClick={handleCloseCouponSuccess}>
						Return to Dashboard
					</Button>
				</div>
			</CustomModal>
		);
	}

	return (
		<CustomModal
			width={'70%'}
			modalId={MODAL_IDS.CANCEL_REASON_MODAL}
			//@ts-expect-error ignore for now
			onClose={handleClose}
			footer={null}
			title="Cancel your subscription"
		>
			<Form
				form={form}
				onFinish={(values) =>
					handleSubmit({ ...values, subscriptionType: modalData?.subscriptionType })
				}
				layout="vertical"
				name="cancel-sub-reason-form"
			>
				<div className="p-4">
					{subscriptionDetails.allowExtendTrial && onExtendTrial && (
						<div className="mb-4 bg-green-50 p-3 rounded-lg border border-green-100">
							<div className="flex items-start gap-3">
								<Clock className="text-green-500 flex-shrink-0 mt-1" size={20} />
								<div>
									<h3 className="font-semibold text-gray-800 mb-2">Need More Time?</h3>
									<p className="text-gray-600 mb-3 text-sm">
										You're currently on a trial period. Would you like to extend your trial to get
										more time to evaluate the product?
									</p>
									<Button
										type="primary"
										onClick={onExtendTrial}
										loading={isExtendingTrial}
										className="bg-green-600 hover:bg-green-700"
									>
										Extend Trial Period
									</Button>
								</div>
							</div>
						</div>
					)}

					<Form.Item
						required
						label="What was our product missing?"
						name="reason"
						rules={[
							{ required: true, message: 'Please input your reason!' },
							{
								pattern: MIN_10_WORDS_REGEX,
								message: 'Please enter at least 10 words',
							},
						]}
					>
						<Input
							placeholder="Please share your feedback so we can improve"
							className="resize-none"
						/>
					</Form.Item>

					<div className="flex items-center gap-2 mb-4 bg-blue-50 p-2 rounded text-sm">
						<AlertCircle className="text-blue-500" size={16} />
						<span className="text-blue-600">Our whole team reads every answer</span>
					</div>

					<Row gutter={[16, 16]}>
						<Col xs={24} md={showCoupon ? 12 : 24}>
							<div className="border border-gray-200 rounded-lg p-4 bg-white h-[300px] flex flex-col">
								<div className="flex items-center mb-3">
									<CreditCard className="text-gray-400 mr-2" size={16} />
									<span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
										Current Subscription
									</span>
								</div>

								<div className="flex justify-between items-center pb-3 mb-3 border-b border-gray-100">
									<div>
										<p className="text-base font-semibold text-gray-800">
											{subscriptionDetails.plan}
										</p>
										<p className="text-xs text-gray-500">
											Billed {subscriptionDetails.currentPeriodEnd}
										</p>
									</div>
									<p className="text-base font-semibold text-gray-800">
										${subscriptionDetails.subscriptionPrice}
									</p>
								</div>

								<div className="flex items-start flex-1">
									<Calendar className="text-gray-400 mr-2 mt-1 flex-shrink-0" size={16} />
									<div>
										<p className="text-sm text-gray-600">
											Your subscription will be canceled, but is still available until the end of
											your billing period on{' '}
											<span className="font-semibold">{subscriptionDetails.currentPeriodEnd}</span>.
										</p>
										<p className="text-xs text-gray-500 italic mt-2">
											If you change your mind, you can renew your subscription anytime.
										</p>
									</div>
								</div>
							</div>
						</Col>

						{showCoupon && (
							<Col xs={24} md={12}>
								<div className="bg-[#1D4ED8] text-white rounded-lg p-4 h-[300px] flex flex-col">
									<h3 className="text-base font-semibold mb-2">Before you go...</h3>
									{(modalData?.subscriptionType === 'Inbound' &&
										loggedInUser?.inboundRetentionCouponUsed) ||
									(modalData?.subscriptionType === 'Outbound' &&
										loggedInUser?.outboundRetentionCouponUsed) ? (
										<>
											<div className="bg-white/10 rounded-lg p-3 flex-1 flex flex-col items-center justify-center text-center">
												<Tag className="mb-2 p-1" size={24} />
												<h3 className="text-xl font-bold mb-2">Discount Already Used</h3>
												<p className="text-sm opacity-80">
													You've already used the retention discount. This offer is only available
													once per account.
												</p>
											</div>
											<Button
												className="w-full !bg-white/20 !text-white font-semibold border-none h-auto py-2 opacity-70 cursor-not-allowed"
												disabled
											>
												Discount Already Applied
											</Button>
										</>
									) : (
										<>
											<div className="flex w-fit items-center px-3 py-1 bg-white/20 rounded-full mb-2">
												<Tag className="mr-1" size={14} />
												<span className="text-sm font-semibold">
													Save ${((subscriptionDetails?.subscriptionPrice || 0) / 2).toFixed(2)}
												</span>
											</div>
											<h3 className="text-xl font-bold mb-3">
												50% off for 1 {subscriptionDetails.subscriptionInterval}
											</h3>
											<div className="bg-white/10 rounded-lg p-3 mb-3 flex-1">
												<p className="text-sm">
													By clicking redeem discount you agree to pay $
													{((subscriptionDetails?.subscriptionPrice || 0) / 2).toFixed(2)} for{' '}
													{subscriptionDetails.plan} until {calculateUntilDate(subscriptionDetails)}
													. After that, you will be charged ${subscriptionDetails.subscriptionPrice}{' '}
													per {subscriptionDetails.subscriptionInterval}. You can still cancel at
													any time.
												</p>
											</div>
											<Button
												className="w-full !bg-white !text-black font-semibold border-none h-auto py-2"
												disabled={
													retentionDiscountMutation.isPending ||
													(modalData?.subscriptionType === 'Inbound' &&
														loggedInUser?.inboundRetentionCouponUsed) ||
													(modalData?.subscriptionType === 'Outbound' &&
														loggedInUser?.outboundRetentionCouponUsed)
												}
												loading={retentionDiscountMutation.isPending}
												onClick={() => retentionDiscountMutation.mutate()}
											>
												Redeem discount
											</Button>
										</>
									)}
								</div>
							</Col>
						)}
					</Row>

					<div className="mt-4 bg-blue-50 p-3 rounded-lg flex items-start gap-3">
						<MessageSquare className="text-blue-500 flex-shrink-0 mt-1" size={20} />
						<div>
							<p className="text-gray-800 mb-3 text-sm">
								Having trouble finding what you need? Let us help! Schedule a one-on-one call with
								our product expert to get personalized guidance.
							</p>
							<Button
								type="default"
								onClick={handleExpertCall}
								className="border-blue-500 text-blue-600 hover:bg-blue-50"
							>
								Book a Call with Expert
							</Button>
						</div>
					</div>

					<Row gutter={16} className="mt-4">
						<Col span={12}>
							<Button type="default" onClick={handleClose} block>
								Cancel
							</Button>
						</Col>
						<Col span={12}>
							<Button
								type="primary"
								danger
								htmlType="submit"
								loading={mutation.isPending || loading}
								block
							>
								Cancel subscription
							</Button>
						</Col>
					</Row>
				</div>
			</Form>

			<div className="px-4 py-3 text-center text-gray-500 text-sm bg-gray-50 border-t border-gray-200">
				Your feedback helps us improve our product for everyone.
			</div>
		</CustomModal>
	);
};

export default CancelSubReasonModal;
